/**
 * Push Notification Service
 * Tích hợp với CongCuMarketing Backend Push API
 */
import api from './api'

// ============================================
// CACHE
// ============================================
let cachedVapidKey = null

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Convert VAPID key từ base64 sang Uint8Array
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4)
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')

  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

/**
 * Convert ArrayBuffer sang base64 string
 */
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return window.btoa(binary)
}

/**
 * Detect device type
 */
function detectDeviceType() {
  const ua = navigator.userAgent.toLowerCase()
  if (/mobile|android|iphone|ipad|ipod/.test(ua)) {
    if (/ipad|tablet/.test(ua)) return 'tablet'
    return 'mobile'
  }
  return 'desktop'
}

/**
 * Detect browser
 */
function detectBrowser() {
  const ua = navigator.userAgent.toLowerCase()
  if (ua.includes('edg/')) return 'Edge'
  if (ua.includes('chrome')) return 'Chrome'
  if (ua.includes('firefox')) return 'Firefox'
  if (ua.includes('safari')) return 'Safari'
  if (ua.includes('opera')) return 'Opera'
  return 'Unknown'
}

/**
 * Detect OS
 */
function detectOS() {
  const ua = navigator.userAgent.toLowerCase()
  if (/iphone|ipad|ipod/.test(ua)) return 'iOS'
  if (/android/.test(ua)) return 'Android'
  if (/windows/.test(ua)) return 'Windows'
  if (/macintosh|mac os x/.test(ua)) return 'macOS'
  if (/linux/.test(ua)) return 'Linux'
  return 'Unknown'
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * Lấy VAPID public key từ backend
 */
export async function getVapidKey() {
  if (cachedVapidKey) return cachedVapidKey

  try {
    const response = await api.get('/push/vapid-key')
    if (response.data.status === 'success' && response.data.data?.vapid_public_key) {
      cachedVapidKey = response.data.data.vapid_public_key
      return cachedVapidKey
    }
    return null
  } catch (error) {
    console.error('[Push] Failed to get VAPID key:', error)
    return null
  }
}

/**
 * Kiểm tra push notifications có được enable không
 */
export async function isPushEnabled() {
  try {
    const response = await api.get('/push/vapid-key')
    return response.data.status === 'success' && response.data.data?.is_enabled
  } catch {
    return false
  }
}

// ============================================
// BROWSER SUPPORT FUNCTIONS
// ============================================

/**
 * Kiểm tra browser có hỗ trợ Push Notification không
 */
export function isPushSupported() {
  return 'serviceWorker' in navigator &&
         'PushManager' in window &&
         'Notification' in window
}

/**
 * Kiểm tra trạng thái permission hiện tại
 * @returns {'granted' | 'denied' | 'default' | 'unsupported'}
 */
export function getNotificationPermission() {
  if (!('Notification' in window)) {
    return 'unsupported'
  }
  return Notification.permission
}

/**
 * Kiểm tra user đã subscribe chưa
 */
export async function isSubscribed() {
  if (!isPushSupported()) return false

  try {
    // Tìm sw-push.js registration trước
    let registration = await navigator.serviceWorker.getRegistration('/sw-push.js')
    // Fallback về bất kỳ registration nào
    if (!registration) {
      registration = await navigator.serviceWorker.getRegistration()
    }
    if (!registration) return false

    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Timeout')), 3000)
    )

    const subscription = await Promise.race([
      registration.pushManager.getSubscription(),
      timeoutPromise
    ])

    return subscription !== null
  } catch (error) {
    console.error('[Push] Error checking subscription:', error)
    return false
  }
}

/**
 * Lấy current subscription endpoint
 */
export async function getCurrentSubscription() {
  if (!isPushSupported()) return null

  try {
    // Tìm sw-push.js registration trước
    let registration = await navigator.serviceWorker.getRegistration('/sw-push.js')
    // Fallback về bất kỳ registration nào
    if (!registration) {
      registration = await navigator.serviceWorker.getRegistration()
    }
    if (!registration) return null

    return await registration.pushManager.getSubscription()
  } catch {
    return null
  }
}

// ============================================
// SUBSCRIPTION FUNCTIONS
// ============================================

/**
 * Đăng ký Service Worker cho push
 * Sử dụng sw-push.js riêng để xử lý push notifications
 */
async function registerPushServiceWorker() {
  try {
    // Kiểm tra xem sw-push.js đã được đăng ký chưa
    let registration = await navigator.serviceWorker.getRegistration('/sw-push.js')

    if (!registration) {
      // Đăng ký sw-push.js
      console.log('[Push] Registering sw-push.js...')
      registration = await navigator.serviceWorker.register('/sw-push.js', {
        scope: '/',
      })
      console.log('[Push] sw-push.js registered:', registration)
    }

    // Đợi service worker active
    if (registration.installing) {
      await new Promise((resolve) => {
        registration.installing.addEventListener('statechange', function() {
          if (this.state === 'activated') {
            resolve()
          }
        })
      })
    } else if (registration.waiting) {
      // Force activate nếu đang waiting
      registration.waiting.postMessage({ type: 'SKIP_WAITING' })
      await new Promise((resolve) => setTimeout(resolve, 100))
    }

    // Đảm bảo có active service worker
    if (!registration.active) {
      await navigator.serviceWorker.ready
    }

    return registration
  } catch (error) {
    console.error('[Push] SW registration failed:', error)
    throw error
  }
}

/**
 * Đăng ký nhận push notification
 *
 * @param {Object} options - Tùy chọn
 * @param {Object} options.preferences - { locations: [], categories: [], frequency: 'realtime' }
 *
 * @returns {Promise<{success: boolean, message: string, subscriptionId?: number}>}
 */
export async function subscribeToPush(options = {}) {
  // 1. Kiểm tra support
  if (!isPushSupported()) {
    return {
      success: false,
      message: 'Trình duyệt của bạn không hỗ trợ thông báo đẩy'
    }
  }

  try {
    // 2. Lấy VAPID key từ backend
    const vapidKey = await getVapidKey()
    if (!vapidKey) {
      return {
        success: false,
        message: 'Chức năng thông báo chưa được cấu hình'
      }
    }

    // 3. Xin permission
    const permission = await Notification.requestPermission()

    if (permission === 'denied') {
      return {
        success: false,
        message: 'Bạn đã từ chối nhận thông báo. Vui lòng bật lại trong cài đặt trình duyệt.'
      }
    }

    if (permission !== 'granted') {
      return {
        success: false,
        message: 'Chưa cấp quyền thông báo'
      }
    }

    // 4. Đăng ký Service Worker
    const registration = await registerPushServiceWorker()

    // 5. Tạo subscription với VAPID key
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey)
    })

    console.log('[Push] Subscription created:', subscription)

    // 6. Gửi subscription lên backend
    const response = await api.post('/push/subscribe', {
      subscription: {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
          auth: arrayBufferToBase64(subscription.getKey('auth'))
        }
      },
      preferences: options.preferences || {},
      device_info: {
        user_agent: navigator.userAgent,
        device_type: detectDeviceType(),
        browser: detectBrowser(),
        os: detectOS()
      }
    })

    if (response.data.status === 'success') {
      // Lưu subscription id vào localStorage
      if (response.data.data?.subscription_id) {
        localStorage.setItem('push_subscription_id', response.data.data.subscription_id)
      }

      return {
        success: true,
        message: response.data.message || 'Đăng ký thành công!',
        subscriptionId: response.data.data?.subscription_id,
        preferences: response.data.data?.preferences
      }
    } else {
      return {
        success: false,
        message: response.data.message || 'Lỗi khi đăng ký thông báo'
      }
    }

  } catch (error) {
    console.error('[Push] Subscribe error:', error)
    return {
      success: false,
      message: `Lỗi: ${error.message}`
    }
  }
}

/**
 * Hủy đăng ký push notification
 */
export async function unsubscribeFromPush() {
  try {
    const subscription = await getCurrentSubscription()

    if (!subscription) {
      return {
        success: true,
        message: 'Bạn chưa đăng ký thông báo'
      }
    }

    // Hủy subscription phía browser
    await subscription.unsubscribe()

    // Thông báo backend xóa subscription
    try {
      await api.post('/push/unsubscribe', {
        endpoint: subscription.endpoint
      })
    } catch (e) {
      console.warn('[Push] Backend unsubscribe failed:', e)
    }

    // Xóa local storage
    localStorage.removeItem('push_subscription_id')

    return {
      success: true,
      message: 'Đã tắt thông báo'
    }

  } catch (error) {
    console.error('[Push] Unsubscribe error:', error)
    return {
      success: false,
      message: `Lỗi: ${error.message}`
    }
  }
}

/**
 * Lấy trạng thái subscription hiện tại từ backend
 */
export async function getSubscriptionStatus() {
  try {
    const subscription = await getCurrentSubscription()

    if (!subscription) {
      return {
        isSubscribed: false
      }
    }

    // Lấy thông tin từ backend
    const response = await api.post('/push/status', {
      endpoint: subscription.endpoint
    })

    if (response.data.status === 'success') {
      return {
        isSubscribed: response.data.data?.is_subscribed || false,
        endpoint: subscription.endpoint,
        ...response.data.data
      }
    }

    return {
      isSubscribed: true,
      endpoint: subscription.endpoint
    }

  } catch (error) {
    console.error('[Push] Get status error:', error)
    return {
      isSubscribed: false,
      error: error.message
    }
  }
}

/**
 * Cập nhật preferences (locations, categories filter)
 */
export async function updatePreferences(preferences) {
  try {
    const subscription = await getCurrentSubscription()

    if (!subscription) {
      return {
        success: false,
        message: 'Chưa đăng ký thông báo'
      }
    }

    const response = await api.put('/push/preferences', {
      endpoint: subscription.endpoint,
      preferences: preferences
    })

    return {
      success: response.data.status === 'success',
      message: response.data.message || (response.data.status === 'success' ? 'Cập nhật thành công' : 'Lỗi cập nhật'),
      preferences: response.data.data?.preferences
    }

  } catch (error) {
    console.error('[Push] Update preferences error:', error)
    return {
      success: false,
      message: `Lỗi: ${error.message}`
    }
  }
}

/**
 * Test local notification (để debug)
 */
export function testLocalNotification() {
  if (Notification.permission !== 'granted') {
    console.warn('[Push] Permission not granted')
    return false
  }

  new Notification('Test Notification', {
    body: 'Đây là test notification từ CongCuMarketing',
    icon: '/icon-192.png',
    badge: '/badge-72.png'
  })
  return true
}

// ============================================
// ADMIN FUNCTIONS
// ============================================

/**
 * [Admin] Lấy cấu hình push
 */
export async function getConfig() {
  const response = await api.get('/push/admin/config')
  return response.data
}

/**
 * [Admin] Khởi tạo cấu hình push
 */
export async function initConfig(data = {}) {
  const response = await api.post('/push/admin/config', data)
  return response.data
}

/**
 * [Admin] Cập nhật cấu hình
 */
export async function updateConfig(data) {
  const response = await api.put('/push/admin/config', data)
  return response.data
}

/**
 * [Admin] Regenerate VAPID keys
 */
export async function regenerateVapid() {
  cachedVapidKey = null // Clear cache
  const response = await api.post('/push/admin/regenerate-vapid')
  return response.data
}

/**
 * [Admin] Regenerate webhook secret
 */
export async function regenerateWebhookSecret() {
  const response = await api.post('/push/admin/regenerate-webhook-secret')
  return response.data
}

/**
 * [Admin] Lấy danh sách subscriptions
 */
export async function getSubscriptions(params = {}) {
  const response = await api.get('/push/admin/subscriptions', { params })
  return response.data
}

/**
 * [Admin] Xóa subscription
 */
export async function deleteSubscription(subscriptionId) {
  const response = await api.delete(`/push/admin/subscriptions/${subscriptionId}`)
  return response.data
}

/**
 * [Admin] Cleanup subscriptions
 */
export async function cleanupSubscriptions(options = {}) {
  const response = await api.post('/push/admin/subscriptions/cleanup', options)
  return response.data
}

/**
 * [Admin] Lấy lịch sử notifications
 */
export async function getNotifications(params = {}) {
  const response = await api.get('/push/admin/notifications', { params })
  return response.data
}

/**
 * [Admin] Gửi push thủ công
 */
export async function sendNotification(data) {
  const response = await api.post('/push/admin/send', data)
  return response.data
}

/**
 * [Admin] Test push đến subscription cụ thể
 */
export async function testPush(subscriptionId, data = {}) {
  const response = await api.post('/push/admin/test', {
    subscription_id: subscriptionId,
    ...data
  })
  return response.data
}

/**
 * [Admin] Lấy thống kê
 */
export async function getStats(period = '7d') {
  const response = await api.get('/push/admin/stats', { params: { period } })
  return response.data
}

/**
 * [Admin] Lấy webhook logs
 */
export async function getWebhookLogs(params = {}) {
  const response = await api.get('/push/admin/webhooks', { params })
  return response.data
}

// ============================================
// DEFAULT EXPORT
// ============================================
const pushNotificationService = {
  // Config
  getVapidKey,
  isPushEnabled,

  // Browser support
  isPushSupported,
  getNotificationPermission,
  isSubscribed,
  getCurrentSubscription,

  // User actions
  subscribeToPush,
  unsubscribeFromPush,
  getSubscriptionStatus,
  updatePreferences,
  testLocalNotification,

  // Admin
  getConfig,
  initConfig,
  updateConfig,
  regenerateVapid,
  regenerateWebhookSecret,
  getSubscriptions,
  deleteSubscription,
  cleanupSubscriptions,
  getNotifications,
  sendNotification,
  testPush,
  getStats,
  getWebhookLogs,
}

export default pushNotificationService
